/**
 * ASCII Art Generator - 内置默认预设
 *
 * 提供若干预设风格供用户快速使用
 */

import type { Preset } from './types'
import type { FullRenderSettings } from '../types'
import type { EffectsState } from '../effects/types'
import { DEFAULT_SETTINGS } from '../defaults'
import { DEFAULT_EFFECTS_STATE } from '../effects/types'

// ============================================
// 辅助函数：深拷贝默认值
// ============================================

function cloneDefaultSettings(): FullRenderSettings {
  return { ...DEFAULT_SETTINGS }
}

function cloneDefaultEffectsState(): EffectsState {
  return JSON.parse(JSON.stringify(DEFAULT_EFFECTS_STATE))
}

// ============================================
// 内置预设定义
// ============================================

/**
 * 复古 CRT 预设
 */
const retroCRTPreset: Preset = {
  id: 'builtin-retro-crt',
  name: '复古 CRT',
  description: '经典CRT显示器效果，扫描线+晕影+色散',
  category: 'builtin',
  createdAt: 0,
  settings: (() => {
    const s = cloneDefaultSettings()
    s.style = 'classic'
    s.colorMode = 'green'
    s.fontSize = 10
    s.contrast = 1.2
    return s
  })(),
  effectsState: (() => {
    const e = cloneDefaultEffectsState()
    e.crt = { enabled: true, config: { scanlineStrength: 0.6, scanlineSpacing: 2, curvature: 0.3, edgeRgbShift: 6, crtVignette: 0.5, time: 0 } }
    e.vignette = { enabled: true, config: { strength: 0.5, radius: 0.6, softness: 0.3 } }
    e.order = ['vignette', 'crt', 'noise', 'waves', 'intervals', 'glitch', 'matrix', 'beam']
    return e
  })(),
}

/**
 * 赛博朋克预设
 */
const cyberpunkPreset: Preset = {
  id: 'builtin-cyberpunk',
  name: '赛博朋克',
  description: '霓虹故障风格，RGB色散+故障效果',
  category: 'builtin',
  createdAt: 0,
  settings: (() => {
    const s = cloneDefaultSettings()
    s.style = 'dither'
    s.colorMode = 'color'
    s.fontSize = 8
    s.contrast = 1.3
    return s
  })(),
  effectsState: (() => {
    const e = cloneDefaultEffectsState()
    e.glitch = { enabled: true, config: { rgbShift: 12, blockShift: 20, blockCount: 5, speed: 2, direction: 'right', time: 0 } }
    e.waves = { enabled: true, config: { strength: 0.4, frequency: 0.1, speed: 2, hueShift: 120, waveCount: 3, time: 0 } }
    e.noise = { enabled: true, config: { strength: 0.3, scale: 3, density: 0.5, colored: true, time: 0 } }
    e.order = ['noise', 'waves', 'glitch', 'vignette', 'crt', 'intervals', 'matrix', 'beam']
    return e
  })(),
}

/**
 * 极简黑白预设
 */
const minimalPreset: Preset = {
  id: 'builtin-minimal',
  name: '极简黑白',
  description: '简洁干净的黑白ASCII风格',
  category: 'builtin',
  createdAt: 0,
  settings: (() => {
    const s = cloneDefaultSettings()
    s.style = 'classic'
    s.colorMode = 'grayscale'
    s.fontSize = 12
    s.charSpacing = 1.2
    s.contrast = 1.1
    return s
  })(),
  effectsState: cloneDefaultEffectsState(),
}

/**
 * Matrix 数字雨预设
 */
const matrixPreset: Preset = {
  id: 'builtin-matrix',
  name: 'Matrix 数字雨',
  description: '经典黑客帝国数字雨效果',
  category: 'builtin',
  createdAt: 0,
  settings: (() => {
    const s = cloneDefaultSettings()
    s.style = 'terminal'
    s.colorMode = 'green'
    s.fontSize = 10
    return s
  })(),
  effectsState: (() => {
    const e = cloneDefaultEffectsState()
    e.matrix = { enabled: true, config: { strength: 0.8, speed: 2, charSize: 14, trailLength: 25, density: 0.7, mode: 'overlay', time: 0 } }
    e.vignette = { enabled: true, config: { strength: 0.4, radius: 0.7, softness: 0.3 } }
    e.order = ['vignette', 'matrix', 'noise', 'waves', 'intervals', 'glitch', 'crt', 'beam']
    return e
  })(),
}

/**
 * 电影感预设
 */
const cinematicPreset: Preset = {
  id: 'builtin-cinematic',
  name: '电影感',
  description: '电影画面风格，晕影+扫描光束',
  category: 'builtin',
  createdAt: 0,
  settings: (() => {
    const s = cloneDefaultSettings()
    s.style = 'classic'
    s.colorMode = 'grayscale'
    s.fontSize = 10
    s.contrast = 1.15
    return s
  })(),
  effectsState: (() => {
    const e = cloneDefaultEffectsState()
    e.vignette = { enabled: true, config: { strength: 0.7, radius: 0.55, softness: 0.4 } }
    e.beam = { enabled: true, config: { strength: 0.5, width: 80, speed: 0.8, direction: 'right', color: '#ffffff', time: 0 } }
    e.order = ['vignette', 'beam', 'noise', 'waves', 'intervals', 'glitch', 'crt', 'matrix']
    return e
  })(),
}

/**
 * 点阵艺术预设
 */
const halftonePreset: Preset = {
  id: 'builtin-halftone',
  name: '点阵艺术',
  description: '半调点阵印刷风格',
  category: 'builtin',
  createdAt: 0,
  settings: (() => {
    const s = cloneDefaultSettings()
    s.style = 'halftone'
    s.colorMode = 'grayscale'
    s.halftoneSize = 1.5
    s.halftoneShape = 'circle'
    return s
  })(),
  effectsState: (() => {
    const e = cloneDefaultEffectsState()
    e.vignette = { enabled: true, config: { strength: 0.3, radius: 0.75, softness: 0.25 } }
    e.order = ['vignette', 'noise', 'waves', 'intervals', 'glitch', 'crt', 'matrix', 'beam']
    return e
  })(),
}

/**
 * 终端风格预设
 */
const terminalPreset: Preset = {
  id: 'builtin-terminal',
  name: '终端风格',
  description: '复古计算机终端效果',
  category: 'builtin',
  createdAt: 0,
  settings: (() => {
    const s = cloneDefaultSettings()
    s.style = 'terminal'
    s.colorMode = 'green'
    s.fontSize = 10
    s.terminalCharset = 'binary'
    return s
  })(),
  effectsState: (() => {
    const e = cloneDefaultEffectsState()
    e.crt = { enabled: true, config: { scanlineStrength: 0.3, scanlineSpacing: 3, curvature: 0.1, edgeRgbShift: 2, crtVignette: 0.3, time: 0 } }
    e.noise = { enabled: true, config: { strength: 0.15, scale: 4, density: 0.4, colored: false, time: 0 } }
    e.order = ['noise', 'crt', 'vignette', 'waves', 'intervals', 'glitch', 'matrix', 'beam']
    return e
  })(),
}

/**
 * 琥珀终端预设
 */
const amberTerminalPreset: Preset = {
  id: 'builtin-amber-terminal',
  name: '琥珀终端',
  description: '经典琥珀色单色显示器风格',
  category: 'builtin',
  createdAt: 0,
  settings: (() => {
    const s = cloneDefaultSettings()
    s.style = 'classic'
    s.colorMode = 'amber'
    s.fontSize = 10
    s.contrast = 1.1
    return s
  })(),
  effectsState: (() => {
    const e = cloneDefaultEffectsState()
    e.crt = { enabled: true, config: { scanlineStrength: 0.25, scanlineSpacing: 3, curvature: 0.08, edgeRgbShift: 1, crtVignette: 0.2, time: 0 } }
    e.order = ['crt', 'vignette', 'noise', 'waves', 'intervals', 'glitch', 'matrix', 'beam']
    return e
  })(),
}

/**
 * 粒子爆炸预设
 */
const particlesPreset: Preset = {
  id: 'builtin-particles',
  name: '粒子爆炸',
  description: '动态粒子效果风格',
  category: 'builtin',
  createdAt: 0,
  settings: (() => {
    const s = cloneDefaultSettings()
    s.style = 'particles'
    s.colorMode = 'grayscale'
    s.particleDensity = 0.6
    s.particleChar = '*'
    return s
  })(),
  effectsState: (() => {
    const e = cloneDefaultEffectsState()
    e.vignette = { enabled: true, config: { strength: 0.4, radius: 0.65, softness: 0.35 } }
    e.order = ['vignette', 'noise', 'waves', 'intervals', 'glitch', 'crt', 'matrix', 'beam']
    return e
  })(),
}

// ============================================
// 导出所有内置预设
// ============================================

/**
 * 所有内置预设列表
 */
export const BUILTIN_PRESETS: Preset[] = [
  minimalPreset,
  retroCRTPreset,
  cyberpunkPreset,
  matrixPreset,
  cinematicPreset,
  halftonePreset,
  terminalPreset,
  amberTerminalPreset,
  particlesPreset,
]

/**
 * 获取内置预设（用于初始化）
 */
export function getBuiltinPresets(): Preset[] {
  return BUILTIN_PRESETS
}